import React, { FC, createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useLocation, useParams } from "react-router";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { InvoiceApi, OrderApi, OrderShipApi } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import {
	TransactionContext,
	TransactionResourceType,
} from "../../../../../../components/Transaction/Context/TransactionContext";
import OrderPreview from "../../components/OrderPreview/OrderPreview";

interface MatchParams {
	orderId: string;
}

interface OrderContextType {
	order: OrderApi;
	setOrder: (o: OrderApi) => void;
	fetchOrder: () => void;
	fetchOrderInvoices: () => Promise<void>;
	customFieldsConfig?: CustomFieldTemplateApi[];
	orderInvoices?: InvoiceApi[];
	orderInvoiceCustomFieldsConfig?: CustomFieldTemplateApi[];
	orderShips?: OrderShipApi[];
	fetchOrderShips?: () => Promise<void>;
}

export const OrderContext = createContext<OrderContextType>({
	order: {} as OrderApi,
	setOrder: (order: OrderApi) => {},
	fetchOrder: () => {},
	customFieldsConfig: {} as CustomFieldTemplateApi[],
	fetchOrderInvoices: async () => {},
});

const OrganizationOrderOrdersPreviewPage: FC<RouteComponentProps> = (props) => {
	const [order, setOrder] = useState<OrderApi | undefined>(undefined);
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[] | undefined>(undefined);
	const { addFlash } = useFlash();
	const { orderId } = useParams<MatchParams>();
	const { setListItemId, setTransactionResourceType } = useContext(TransactionContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();
	const [invoices, setInvoices] = useState<InvoiceApi[] | undefined>();
	const [orderInvoiceCustomFieldsConfig, setOrderInvoiceCustomFieldsConfig] = useState<
		CustomFieldTemplateApi[] | undefined
	>();
	const location = useLocation();
	const history = useHistory();
	const [orderShips, setOrderShips] = useState<OrderShipApi[] | undefined>();

	useEffect(() => {
		handleInit();
	}, []);

	useEffect(() => {
		if (order?.shippable) {
			fetchOrderShips();
		} else {
			setOrderShips([]);
		}
	}, [order?.shippable]);

	const handleInit = async () => {
		await fetchOrder();
		setListItemId(orderId);
		setTransactionResourceType(TransactionResourceType.SALES);
	};

	const fetchOrder = async () => {
		try {
			const params = { include: "all" };
			const customFieldsParams = {
				f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=SALES_ORDER`),
			};

			const requests: [Promise<OrderApi>, Promise<CustomFieldTemplateApi[]>] = [
				api.organization().getOrder(orderId, params),
				api.organization().getCustomFields(customFieldsParams),
			];
			const [newOrder, newCustomFieldsConfig] = await Promise.all(requests);
			setOrder(newOrder);
			setCustomFieldsConfig(newCustomFieldsConfig);
			handleChangeTabTitle(`${newOrder.document_number} | ${t("modules.order.header.title")}`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchOrderInvoices = async () => {
		if (!order) return;

		try {
			const params = {
				include: "all",
				f: btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}orderIds=${order.id}`))),
			};
			const customFieldsParams = {
				f: btoa(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=INVOICE`),
			};

			const requests: [Promise<InvoiceApi[]>, Promise<CustomFieldTemplateApi[]>] = [
				apiOrganization.getInvoices(params),
				apiOrganization.getCustomFields(customFieldsParams),
			];
			const [orderInvoicesResponse, customFieldsResponse] = await Promise.all(requests);
			setInvoices(orderInvoicesResponse);
			setOrderInvoiceCustomFieldsConfig(customFieldsResponse);
			if (location.pathname.endsWith("invoices")) {
				if (orderInvoicesResponse.length > 0) {
					const invoiceId = orderInvoicesResponse[orderInvoicesResponse.length - 1].id;
					history.replace(`${props.location.pathname}/${invoiceId}`);
				} else {
					history.replace(`${props.location.pathname}/new`);
				}
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchOrderShips = async () => {
		try {
			const res = await apiOrganization.getOrderShips(orderId, { include: "items,items.product" });
			setOrderShips(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!order?.id || !orderShips) return <LoadingContainer />;

	return (
		<OrderContext.Provider
			value={{
				order,
				setOrder,
				fetchOrder,
				customFieldsConfig,
				orderInvoices: invoices,
				fetchOrderInvoices,
				orderInvoiceCustomFieldsConfig,
				orderShips,
				fetchOrderShips,
			}}
		>
			<OrderPreview />
		</OrderContext.Provider>
	);
};

export default OrganizationOrderOrdersPreviewPage;
